import type { PropsWithChildren } from "react";
import React, { createContext, useMemo } from "react";

import type {
  HexColorDto,
  ProfileDto,
  MovieContentDto,
  EpisodeContentDto,
  SeriesContentDto,
  LinearContentDto,
  AboutDto,
  SearchResultsDto,
  MenuDto,
  WatchlistDto,
  TranslationsDto,
  ThemeDto,
  SearchRecommendationsDto,
  LoginDto,
  GeneratePairingCodeDto,
  CheckPairingCodeDto,
  RefreshDto,
  AccountDto,
  PageDto,
  SubscriptionDto,
  SubscriptionOffersDto,
  EPGDto,
  EPGConfigDto,
} from "$src/core/models/dto";

type PrivateResponseErrorInterceptor = (
  error: { _retry?: boolean; status?: number } & Record<string, unknown>,
  retry: () => Promise<unknown>,
) => Promise<unknown>;

export type ApiServices = {
  /** Init Data (public) */
  getTranslations?: () => Promise<TranslationsDto>;
  getTheme?: () => Promise<ThemeDto>;
  getMenu: () => Promise<MenuDto>;
  getAbout: () => Promise<AboutDto>;

  /** Dynamic Content (public) */
  getPage: (slug: string) => Promise<PageDto>;
  getStorefront: () => Promise<PageDto>;

  getMovieContent: (id: string) => Promise<MovieContentDto>;
  getSeriesContent: (id: string) => Promise<SeriesContentDto>;
  getEpisodeContent: (id: string) => Promise<EpisodeContentDto>;
  getLinearContent: (id: string) => Promise<LinearContentDto>;

  getEPGConfig: () => Promise<EPGConfigDto>;
  getEPG: (options: {
    daysBefore: number;
    daysAfter: number;
    timeZone: string;
  }) => Promise<EPGDto>;

  getSearchResults: (params?: {
    query?: string;
    tags?: string[];
  }) => Promise<SearchResultsDto>;
  getSearchRecommendations: () => Promise<SearchRecommendationsDto>;

  /** Auth */
  login: (email: string, password: string) => Promise<LoginDto>;
  register?: (
    email: string,
    password: string,
    details?: { firstName?: string; lastName?: string },
  ) => Promise<LoginDto>;
  refresh: (refreshToken: string) => Promise<RefreshDto>;
  generatePairingCode: () => Promise<GeneratePairingCodeDto>;
  authorizePairingCode: (code: string) => Promise<boolean>;
  checkPairingCode: (code: string) => Promise<CheckPairingCodeDto>;

  /** Account */
  getAccount: () => Promise<AccountDto>;
  getSubscription: () => Promise<SubscriptionDto | null>;
  getSubscriptionOffers: () => Promise<SubscriptionOffersDto>;
  activateSubscriptionOffer: (
    offerId: string,
    coupon?: string,
  ) => Promise<void>;
  switchSubscriptionOffer: (
    type: "upgrade" | "downgrade",
    offerId: string,
    toOfferId: string,
    coupon?: string,
  ) => Promise<void>;
  cancelSubscription: (id: string) => Promise<void>;

  /** Profile */
  getProfiles: () => Promise<ProfileDto[]>;
  createProfile: (name: string, tint: HexColorDto) => Promise<ProfileDto>;
  updateProfile: (
    id: string,
    profile: { name: string; tint: HexColorDto },
  ) => Promise<ProfileDto>;
  deleteProfile: (id: string) => Promise<void>;
  getWatchlist: (profileId: string) => Promise<WatchlistDto>;
  addWatchlistItem: (profileId: string, id: string) => Promise<void>;
  removeWatchlistItem: (profileId: string, id: string) => Promise<void>;

  /** Interceptors */
  onPrivateResponseInterceptor?: (
    onError: PrivateResponseErrorInterceptor,
  ) => () => void;
};

export type ApiContextType = {
  api: ApiServices;
};

export const ApiContext = createContext<ApiContextType | null>(null);

const ApiProvider = ({ children, api }: PropsWithChildren<ApiContextType>) => {
  const value = useMemo<ApiContextType>(
    () => ({
      api,
    }),
    [api],
  );

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

export default ApiProvider;
